import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Edit2, Trash2, Upload, Music, FolderOpen, 
  Drum, Save, X, ChevronRight, Play, Pause, Volume2, ShieldAlert, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Default column labels - these can be customized
const DEFAULT_LABELS = {
  categories: 'Categories',
  ensembles: 'Ensembles', 
  instruments: 'Instruments',
  samples: 'Samples'
};

export default function AdminBatucada() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromParam = new URLSearchParams(location.search).get('from');
  const backTo = fromParam ? decodeURIComponent(fromParam) : '/admin';
  
  // Check for admin access (super_admin or admin)
  useEffect(() => {
    if (user && !['super_admin', 'admin'].includes(user.role)) {
      toast.error('Access Denied: Admin only');
      navigate('/admin/dashboard');
    }
  }, [user, navigate]);
  
  // Column label customization
  const [columnLabels, setColumnLabels] = useState(() => {
    const saved = localStorage.getItem('batucada_column_labels');
    return saved ? JSON.parse(saved) : DEFAULT_LABELS;
  });
  const [showLabelEditor, setShowLabelEditor] = useState(null); // which column is being edited
  const [editingLabel, setEditingLabel] = useState('');
  
  // State
  const [categories, setCategories] = useState([]);
  const [ensembles, setEnsembles] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Selection state
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedEnsemble, setSelectedEnsemble] = useState(null);
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  
  // Modal state
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showEnsembleModal, setShowEnsembleModal] = useState(false);
  const [showInstrumentModal, setShowInstrumentModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({});
  
  // Pending files for instrument creation
  const [pendingFiles, setPendingFiles] = useState([]);
  
  // Audio preview
  const [playingSample, setPlayingSample] = useState(null);
  const audioRef = useRef(null);
  
  // Get auth token for API calls
  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  
  // Column label editing functions
  const startEditingLabel = (columnKey) => {
    setShowLabelEditor(columnKey);
    setEditingLabel(columnLabels[columnKey]);
  };
  
  const saveLabel = () => {
    if (showLabelEditor && editingLabel.trim()) {
      const newLabels = { ...columnLabels, [showLabelEditor]: editingLabel.trim() };
      setColumnLabels(newLabels);
      localStorage.setItem('batucada_column_labels', JSON.stringify(newLabels));
      toast.success(`"${showLabelEditor}" renamed to "${editingLabel.trim()}"`);
    }
    setShowLabelEditor(null);
    setEditingLabel('');
  };
  
  const resetLabels = () => {
    setColumnLabels(DEFAULT_LABELS);
    localStorage.removeItem('batucada_column_labels');
    toast.success('Labels reset to defaults');
  };

  // Load data
  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      console.log('Category selected:', selectedCategory.id, selectedCategory.name);
      loadEnsembles(selectedCategory.id);
    } else {
      console.log('No category selected');
      setEnsembles([]);
      setSelectedEnsemble(null);
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedEnsemble) {
      loadInstruments(selectedEnsemble.id);
    } else {
      setInstruments([]);
      setSelectedInstrument(null);
    }
  }, [selectedEnsemble]);

  useEffect(() => {
    if (selectedInstrument) {
      loadSamples(selectedInstrument.id);
    } else {
      setSamples([]);
    }
  }, [selectedInstrument]);

  const loadCategories = async () => {
    try {
      const res = await axios.get(`${API}/api/batucada/categories`);
      setCategories(res.data);
    } catch (err) {
      toast.error('Failed to load categories');
    }
    setLoading(false);
  };

  const loadEnsembles = async (categoryId) => {
    try {
      const res = await axios.get(`${API}/api/batucada/ensembles?category_id=${categoryId}`);
      setEnsembles(res.data);
    } catch (err) {
      toast.error('Failed to load ensembles');
    }
  };

  const loadInstruments = async (ensembleId) => {
    try {
      const res = await axios.get(`${API}/api/batucada/instruments?ensemble_id=${ensembleId}`);
      setInstruments(res.data);
    } catch (err) {
      toast.error('Failed to load instruments');
    }
  };

  const loadSamples = async (instrumentId) => {
    try {
      const res = await axios.get(`${API}/api/batucada/samples?instrument_id=${instrumentId}`);
      setSamples(res.data);
    } catch (err) {
      toast.error('Failed to load samples');
    }
  };

  // CRUD Operations
  const saveCategory = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/api/batucada/categories/${editingItem.id}`, formData, { headers: getAuthHeaders() });
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/api/batucada/categories`, formData, { headers: getAuthHeaders() });
        toast.success('Category created');
      }
      setShowCategoryModal(false);
      setEditingItem(null);
      setFormData({});
      loadCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save category');
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Delete this category and ALL its ensembles, instruments, and samples?')) return;
    try {
      await axios.delete(`${API}/api/batucada/categories/${id}`, { headers: getAuthHeaders() });
      toast.success('Category deleted');
      setSelectedCategory(null);
      loadCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete category');
    }
  };

  const saveEnsemble = async () => {
    try {
      const categoryId = formData.category_id || selectedCategory?.id;
      if (!categoryId) {
        toast.error('Please select a category');
        return;
      }
      const data = { ...formData, category_id: categoryId };
      if (editingItem) {
        await axios.put(`${API}/api/batucada/ensembles/${editingItem.id}`, data, { headers: getAuthHeaders() });
        toast.success('Ensemble updated');
      } else {
        await axios.post(`${API}/api/batucada/ensembles`, data, { headers: getAuthHeaders() });
        toast.success('Ensemble created');
      }
      setShowEnsembleModal(false);
      setEditingItem(null);
      setFormData({});
      if (categoryId) loadEnsembles(categoryId);
      loadCategories(); // Refresh to show updated counts
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save ensemble');
    }
  };

  const deleteEnsemble = async (id) => {
    if (!window.confirm('Delete this ensemble and ALL its instruments and samples?')) return;
    try {
      await axios.delete(`${API}/api/batucada/ensembles/${id}`, { headers: getAuthHeaders() });
      toast.success('Ensemble deleted');
      setSelectedEnsemble(null);
      loadEnsembles(selectedCategory.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete ensemble');
    }
  };

  const saveInstrument = async () => {
    try {
      const ensembleId = formData.ensemble_id || selectedEnsemble?.id;
      const data = { ...formData, ensemble_id: ensembleId };
      
      let instrumentId;
      if (editingItem) {
        await axios.put(`${API}/api/batucada/instruments/${editingItem.id}`, data, { headers: getAuthHeaders() });
        toast.success('Instrument updated');
        instrumentId = editingItem.id;
      } else {
        const response = await axios.post(`${API}/api/batucada/instruments`, data, { headers: getAuthHeaders() });
        toast.success('Instrument created');
        instrumentId = response.data.id;
      }
      
      // Upload any pending files for this instrument
      if (pendingFiles.length > 0 && instrumentId) {
        toast.info(`Uploading ${pendingFiles.length} sample(s)...`);
        for (const file of pendingFiles) {
          const uploadData = new FormData();
          uploadData.append('file', file);
          uploadData.append('instrument_id', instrumentId);
          uploadData.append('name', file.name);
          uploadData.append('bpm', '120');
          uploadData.append('beats', '4');
          uploadData.append('is_loop', 'true');
          
          try {
            await axios.post(`${API}/api/batucada/upload-sample`, uploadData, { headers: getAuthHeaders() });
            toast.success(`Uploaded: ${file.name}`);
          } catch (err) {
            toast.error(`Failed to upload: ${file.name}`);
          }
        }
        setPendingFiles([]);
      }
      
      setShowInstrumentModal(false);
      setEditingItem(null);
      setFormData({});
      if (ensembleId) loadInstruments(ensembleId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save instrument');
    }
  };

  const deleteInstrument = async (id) => {
    if (!window.confirm('Delete this instrument and ALL its samples?')) return;
    try {
      await axios.delete(`${API}/api/batucada/instruments/${id}`, { headers: getAuthHeaders() });
      toast.success('Instrument deleted');
      setSelectedInstrument(null);
      loadInstruments(selectedEnsemble.id);
    } catch (err) {
      toast.error('Failed to delete instrument');
    }
  };

  const deleteSample = async (id) => {
    if (!window.confirm('Delete this sample?')) return;
    try {
      await axios.delete(`${API}/api/batucada/samples/${id}`, { headers: getAuthHeaders() });
      toast.success('Sample deleted');
      loadSamples(selectedInstrument.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete sample');
    }
  };

  // File upload
  const handleFileUpload = async (files) => {
    // Use instrument from modal dropdown first, then fall back to selected instrument
    const instrumentId = formData.upload_instrument_id || selectedInstrument?.id;
    
    if (!instrumentId) {
      toast.error('Please select an instrument first');
      return;
    }

    let uploadedCount = 0;
    for (const file of files) {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('instrument_id', instrumentId);
      uploadFormData.append('name', file.name);
      uploadFormData.append('bpm', '120');
      uploadFormData.append('beats', '4');
      uploadFormData.append('is_loop', 'true');

      try {
        await axios.post(`${API}/api/batucada/upload-sample`, uploadFormData, { headers: getAuthHeaders() });
        uploadedCount++;
      } catch (err) {
        toast.error(err.response?.data?.detail || `Failed to upload: ${file.name}`);
      }
    }
    
    if (uploadedCount > 0) {
      toast.success(`Uploaded ${uploadedCount} sample(s) successfully!`);
      
      // Find and select the instrument that was uploaded to
      const targetInstrumentId = instrumentId;
      
      // If the instrument isn't currently selected, find it and select the whole chain
      if (!selectedInstrument || selectedInstrument.id !== targetInstrumentId) {
        // Find the instrument to get its ensemble
        const allInstruments = await axios.get(`${API}/api/batucada/instruments`);
        const targetInstrument = allInstruments.data.find(i => i.id === targetInstrumentId);
        
        if (targetInstrument) {
          // Find the ensemble to get its category
          const allEnsembles = await axios.get(`${API}/api/batucada/ensembles`);
          const targetEnsemble = allEnsembles.data.find(e => e.id === targetInstrument.ensemble_id);
          
          if (targetEnsemble) {
            // Find the category
            const targetCategory = categories.find(c => c.id === targetEnsemble.category_id);
            
            // Select the full chain: category -> ensemble -> instrument
            if (targetCategory) {
              setSelectedCategory(targetCategory);
              await loadEnsembles(targetCategory.id);
            }
            setSelectedEnsemble(targetEnsemble);
            await loadInstruments(targetEnsemble.id);
            setSelectedInstrument(targetInstrument);
          }
        }
      }
      
      // Always reload samples for the target instrument
      loadSamples(targetInstrumentId);
    }
    
    setShowUploadModal(false);
    // Clear modal form data
    setFormData(prev => ({...prev, upload_category_id: '', upload_ensemble_id: '', upload_instrument_id: ''}));
  };

  // Audio preview
  const togglePlaySample = (sample) => {
    if (playingSample === sample.id) {
      audioRef.current?.pause();
      setPlayingSample(null);
    } else {
      if (audioRef.current) {
        audioRef.current.src = `${API}${sample.file_url}`;
        audioRef.current.play();
        setPlayingSample(sample.id);
      }
    }
  };

  // Modals
  const openCategoryModal = (item = null) => {
    setEditingItem(item);
    setFormData(item ? { name: item.name, description: item.description, icon: item.icon, order: item.order } : { name: '', description: '', icon: '', order: 0 });
    setShowCategoryModal(true);
  };

  const openEnsembleModal = (item = null) => {
    setEditingItem(item);
    if (item) {
      setFormData({ name: item.name, description: item.description, icon: item.icon, order: item.order, category_id: item.category_id });
    } else {
      // Pre-populate category_id if a category is currently selected
      setFormData({ name: '', description: '', icon: '', order: 0, category_id: selectedCategory?.id || '' });
    }
    setShowEnsembleModal(true);
  };

  const openInstrumentModal = (item = null) => {
    setEditingItem(item);
    if (item) {
      setFormData({ name: item.name, description: item.description, icon: item.icon, order: item.order, ensemble_id: item.ensemble_id });
    } else {
      // Pre-populate ensemble_id if an ensemble is currently selected
      setFormData({ name: '', description: '', icon: '', order: 0, ensemble_id: selectedEnsemble?.id || '' });
    }
    setShowInstrumentModal(true);
  };

  return (
    <div className="min-h-screen bg-stone-950 text-white">
      {/* Hidden audio element */}
      <audio ref={audioRef} onEnded={() => setPlayingSample(null)} />

      {/* Header */}
      <header className="bg-stone-900 border-b border-stone-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={backTo} className="p-2 hover:bg-stone-800 rounded-lg" data-testid="admin-batucada-back">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Drum className="w-7 h-7 text-amber-500" />
                Brasilian Grooves
              </h1>
              <p className="text-sm text-stone-400">Click column headers to rename them • Click items to select • Use + to add</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={resetLabels}
              className="px-3 py-2 bg-stone-700 text-stone-300 rounded-lg text-sm hover:bg-stone-600 flex items-center gap-2"
              title="Reset column names to defaults"
            >
              <Settings className="w-4 h-4" />
              Reset Labels
            </button>
            <Link 
              to="/batucada"
              className="px-4 py-2 bg-amber-500 text-black rounded-lg font-bold hover:bg-amber-400"
            >
              Open Batucada App →
            </Link>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="px-6 py-3 bg-stone-900/50 border-b border-stone-800 flex items-center gap-2 text-sm">
        <span className="text-stone-500">Library</span>
        {selectedCategory && (
          <>
            <ChevronRight className="w-4 h-4 text-stone-600" />
            <span className="text-amber-400">{selectedCategory.name}</span>
          </>
        )}
        {selectedEnsemble && (
          <>
            <ChevronRight className="w-4 h-4 text-stone-600" />
            <span className="text-cyan-400">{selectedEnsemble.name}</span>
          </>
        )}
        {selectedInstrument && (
          <>
            <ChevronRight className="w-4 h-4 text-stone-600" />
            <span className="text-green-400">{selectedInstrument.name}</span>
          </>
        )}
      </div>

      {/* Main Content - 4 Column Layout */}
      <div className="grid grid-cols-4 gap-4 p-6 h-[calc(100vh-140px)]">
        {/* Column 1: Categories */}
        <div className="bg-stone-900 rounded-xl border border-stone-800 flex flex-col">
          <div className="p-4 border-b border-stone-800">
            {showLabelEditor === 'categories' ? (
              <div className="flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-amber-400" />
                <input
                  type="text"
                  value={editingLabel}
                  onChange={(e) => setEditingLabel(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && saveLabel()}
                  className="bg-stone-800 border border-amber-500 rounded px-2 py-1 text-amber-400 font-bold flex-1"
                  autoFocus
                />
                <button onClick={saveLabel} className="p-1 bg-green-600 rounded hover:bg-green-500">
                  <Save className="w-4 h-4" />
                </button>
                <button onClick={() => setShowLabelEditor(null)} className="p-1 bg-stone-700 rounded hover:bg-stone-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button 
                onClick={() => startEditingLabel('categories')}
                className="w-full text-left font-bold text-amber-400 flex items-center gap-2 hover:bg-stone-800 p-2 rounded-lg transition-colors"
                title="Click to edit label"
              >
                <FolderOpen className="w-5 h-5" /> 
                {columnLabels.categories}
                <Edit2 className="w-4 h-4 ml-auto opacity-70" />
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {categories.map(cat => (
              <div
                key={cat.id}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('CLICKED CATEGORY:', cat.id, cat.name);
                  setSelectedCategory(cat);
                }}
                style={{ WebkitTapHighlightColor: 'transparent', userSelect: 'none' }}
                className={`w-full p-3 rounded-lg flex items-center justify-between group cursor-pointer ${
                  selectedCategory?.id === cat.id ? 'bg-amber-500/20 border border-amber-500' : 'hover:bg-stone-800 border border-transparent'
                }`}
                data-testid={`category-row-${cat.id}`}
              >
                <div className="flex items-center gap-2 flex-1">
                  <span className="text-xl">{cat.icon || '📁'}</span>
                  <span className="font-medium">{cat.name}</span>
                </div>
                <div className="flex gap-1 ml-2">
                  <button type="button" onClick={(e) => { e.stopPropagation(); openCategoryModal(cat); }} className="p-1.5 hover:bg-amber-600 bg-stone-700 rounded" title="Edit" data-testid={`edit-category-${cat.id}`}>
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={(e) => { e.stopPropagation(); deleteCategory(cat.id); }} className="p-1.5 hover:bg-red-600 bg-stone-700 rounded text-red-400" title="Delete" data-testid={`delete-category-${cat.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {categories.length === 0 && (
              <p className="text-center text-stone-500 py-8">No categories yet</p>
            )}
          </div>
          {/* Add New Category Button */}
          <div className="p-3 border-t border-stone-800">
            <button
              onClick={() => openCategoryModal()}
              className="w-full py-2 px-4 bg-amber-500/20 text-amber-400 rounded-lg hover:bg-amber-500/30 font-medium flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add New {columnLabels.categories.replace(/s$/, '')}
            </button>
          </div>
        </div>

        {/* Column 2: Ensembles */}
        <div className="bg-stone-900 rounded-xl border border-stone-800 flex flex-col">
          <div className="p-4 border-b border-stone-800">
            {showLabelEditor === 'ensembles' ? (
              <div className="flex items-center gap-2">
                <Music className="w-5 h-5 text-cyan-400" />
                <input
                  type="text"
                  value={editingLabel}
                  onChange={(e) => setEditingLabel(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && saveLabel()}
                  className="bg-stone-800 border border-cyan-500 rounded px-2 py-1 text-cyan-400 font-bold flex-1"
                  autoFocus
                />
                <button onClick={saveLabel} className="p-1 bg-green-600 rounded hover:bg-green-500">
                  <Save className="w-4 h-4" />
                </button>
                <button onClick={() => setShowLabelEditor(null)} className="p-1 bg-stone-700 rounded hover:bg-stone-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button 
                onClick={() => startEditingLabel('ensembles')}
                className="w-full text-left font-bold text-cyan-400 flex items-center gap-2 hover:bg-stone-800 p-2 rounded-lg transition-colors"
                title="Click to edit label"
              >
                <Music className="w-5 h-5" /> 
                {columnLabels.ensembles}
                <Edit2 className="w-4 h-4 ml-auto opacity-70" />
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {!selectedCategory ? (
              <p className="text-center text-stone-500 py-8">Select a category</p>
            ) : ensembles.length === 0 ? (
              <p className="text-center text-stone-500 py-8">No ensembles yet</p>
            ) : (
              ensembles.map(ens => (
                <div
                  key={ens.id}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('CLICKED ENSEMBLE:', ens.id, ens.name);
                    setSelectedEnsemble(ens);
                  }}
                  style={{ WebkitTapHighlightColor: 'transparent', userSelect: 'none' }}
                  className={`w-full p-3 rounded-lg flex items-center justify-between group cursor-pointer ${
                    selectedEnsemble?.id === ens.id ? 'bg-cyan-500/20 border border-cyan-500' : 'hover:bg-stone-800 border border-transparent'
                  }`}
                  data-testid={`ensemble-row-${ens.id}`}
                >
                  <div className="flex items-center gap-2 flex-1">
                    <span className="text-xl">{ens.icon || '🎵'}</span>
                    <span className="font-medium">{ens.name}</span>
                  </div>
                  <div className="flex gap-1 ml-2">
                    <button type="button" onClick={(e) => { e.stopPropagation(); openEnsembleModal(ens); }} className="p-1.5 hover:bg-cyan-600 bg-stone-700 rounded" title="Edit" data-testid={`edit-ensemble-${ens.id}`}>
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); deleteEnsemble(ens.id); }} className="p-1.5 hover:bg-red-600 bg-stone-700 rounded text-red-400" title="Delete" data-testid={`delete-ensemble-${ens.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          {/* Add New Ensemble Button */}
          <div className="p-3 border-t border-stone-800">
            <button
              onClick={() => openEnsembleModal()}
              className="w-full py-2 px-4 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 font-medium flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add New {columnLabels.ensembles.replace(/s$/, '')}
            </button>
          </div>
        </div>

        {/* Column 3: Instruments */}
        <div className="bg-stone-900 rounded-xl border border-stone-800 flex flex-col">
          <div className="p-4 border-b border-stone-800">
            {showLabelEditor === 'instruments' ? (
              <div className="flex items-center gap-2">
                <Drum className="w-5 h-5 text-green-400" />
                <input
                  type="text"
                  value={editingLabel}
                  onChange={(e) => setEditingLabel(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && saveLabel()}
                  className="bg-stone-800 border border-green-500 rounded px-2 py-1 text-green-400 font-bold flex-1"
                  autoFocus
                />
                <button onClick={saveLabel} className="p-1 bg-green-600 rounded hover:bg-green-500">
                  <Save className="w-4 h-4" />
                </button>
                <button onClick={() => setShowLabelEditor(null)} className="p-1 bg-stone-700 rounded hover:bg-stone-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button 
                onClick={() => startEditingLabel('instruments')}
                className="w-full text-left font-bold text-green-400 flex items-center gap-2 hover:bg-stone-800 p-2 rounded-lg transition-colors"
                title="Click to edit label"
              >
                <Drum className="w-5 h-5" /> 
                {columnLabels.instruments}
                <Edit2 className="w-4 h-4 ml-auto opacity-70" />
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {!selectedEnsemble ? (
              <p className="text-center text-stone-500 py-8">Select an ensemble</p>
            ) : instruments.length === 0 ? (
              <p className="text-center text-stone-500 py-8">No instruments yet</p>
            ) : (
              instruments.map(inst => (
                <div
                  key={inst.id}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('CLICKED INSTRUMENT:', inst.id, inst.name);
                    setSelectedInstrument(inst);
                  }}
                  style={{ WebkitTapHighlightColor: 'transparent', userSelect: 'none' }}
                  className={`w-full p-3 rounded-lg flex items-center justify-between group cursor-pointer ${
                    selectedInstrument?.id === inst.id ? 'bg-green-500/20 border border-green-500' : 'hover:bg-stone-800 border border-transparent'
                  }`}
                  data-testid={`instrument-row-${inst.id}`}
                >
                  <div className="flex items-center gap-2 flex-1">
                    <span className="text-xl">{inst.icon || '🥁'}</span>
                    <div>
                      <span className="font-medium">{inst.name}</span>
                      <span className="text-xs text-stone-500 ml-2">{inst.sample_count} samples</span>
                    </div>
                  </div>
                  <div className="flex gap-1 ml-2">
                    <button type="button" onClick={(e) => { e.stopPropagation(); openInstrumentModal(inst); }} className="p-1.5 hover:bg-green-600 bg-stone-700 rounded" title="Edit" data-testid={`edit-instrument-${inst.id}`}>
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); deleteInstrument(inst.id); }} className="p-1.5 hover:bg-red-600 bg-stone-700 rounded text-red-400" title="Delete" data-testid={`delete-instrument-${inst.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          {/* Add New Instrument Button */}
          <div className="p-3 border-t border-stone-800">
            <button
              onClick={() => openInstrumentModal()}
              className="w-full py-2 px-4 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 font-medium flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add New {columnLabels.instruments.replace(/s$/, '')}
            </button>
          </div>
        </div>

        {/* Column 4: Samples */}
        <div className="bg-stone-900 rounded-xl border border-stone-800 flex flex-col">
          <div className="p-4 border-b border-stone-800">
            {showLabelEditor === 'samples' ? (
              <div className="flex items-center gap-2">
                <Volume2 className="w-5 h-5 text-purple-400" />
                <input
                  type="text"
                  value={editingLabel}
                  onChange={(e) => setEditingLabel(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && saveLabel()}
                  className="bg-stone-800 border border-purple-500 rounded px-2 py-1 text-purple-400 font-bold flex-1"
                  autoFocus
                />
                <button onClick={saveLabel} className="p-1 bg-green-600 rounded hover:bg-green-500">
                  <Save className="w-4 h-4" />
                </button>
                <button onClick={() => setShowLabelEditor(null)} className="p-1 bg-stone-700 rounded hover:bg-stone-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button 
                onClick={() => startEditingLabel('samples')}
                className="w-full text-left font-bold text-purple-400 flex items-center gap-2 hover:bg-stone-800 p-2 rounded-lg transition-colors"
                title="Click to edit label"
              >
                <Volume2 className="w-5 h-5" /> 
                {columnLabels.samples}
                <Edit2 className="w-4 h-4 ml-auto opacity-70" />
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            {!selectedInstrument ? (
              <div className="text-center py-8">
                <Volume2 className="w-12 h-12 mx-auto mb-3 text-stone-600" />
                <p className="text-stone-500">Select an instrument to view samples</p>
                <p className="text-xs text-stone-600 mt-1">Or use Upload to add samples directly</p>
              </div>
            ) : samples.length === 0 ? (
              <div className="text-center py-8">
                <Upload className="w-12 h-12 mx-auto mb-3 text-stone-600" />
                <p className="text-stone-500">No samples yet</p>
                <p className="text-xs text-stone-600 mt-1">Upload WAV, MP3, or other audio files</p>
              </div>
            ) : (
              <>
                <div className="text-xs text-stone-500 px-2 pb-1 border-b border-stone-800 mb-2">
                  {samples.length} sample{samples.length !== 1 ? 's' : ''} in {selectedInstrument.name}
                </div>
                {samples.map(sample => (
                  <div
                    key={sample.id}
                    className="p-3 rounded-lg bg-stone-800 hover:bg-stone-750 border border-stone-700 hover:border-purple-500/50 transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      {/* Play button with waveform icon */}
                      <button
                        onClick={() => togglePlaySample(sample)}
                        className={`p-3 rounded-lg transition-all ${
                          playingSample === sample.id 
                            ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/30' 
                            : 'bg-stone-700 hover:bg-purple-500/20 text-purple-400'
                        }`}
                      >
                        {playingSample === sample.id ? (
                          <Pause className="w-5 h-5" />
                        ) : (
                          <Play className="w-5 h-5" />
                        )}
                      </button>
                      
                      {/* Sample info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate text-white">{sample.name}</p>
                        <div className="flex items-center gap-3 text-xs text-stone-400 mt-1">
                          <span className="flex items-center gap-1">
                            <Music className="w-3 h-3" />
                            {sample.bpm} BPM
                          </span>
                          <span>•</span>
                          <span>{sample.beats} beats</span>
                          {sample.is_loop && (
                            <>
                              <span>•</span>
                              <span className="text-purple-400">Loop</span>
                            </>
                          )}
                        </div>
                        {/* Visual waveform placeholder */}
                        <div className="mt-2 h-6 bg-stone-900 rounded flex items-center px-2 gap-0.5">
                          {[...Array(30)].map((_, i) => (
                            <div
                              key={i}
                              className={`w-1 rounded-full transition-all ${
                                playingSample === sample.id ? 'bg-purple-400' : 'bg-stone-600'
                              }`}
                              style={{ 
                                height: `${Math.random() * 16 + 4}px`,
                                animationDelay: `${i * 50}ms`
                              }}
                            />
                          ))}
                        </div>
                      </div>
                      
                      {/* Delete button */}
                      <button 
                        onClick={() => deleteSample(sample.id)} 
                        className="p-2 rounded-lg hover:bg-red-900/50 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Delete sample"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
          {/* Upload Samples Section - at bottom */}
          <div className="p-3 border-t border-stone-800">
            {selectedInstrument ? (
              <div
                className="border-2 border-dashed border-stone-600 hover:border-purple-500 rounded-lg p-4 text-center transition-all cursor-pointer"
                onClick={() => document.getElementById('quick-upload-input').click()}
                onDrop={(e) => {
                  e.preventDefault();
                  handleFileUpload(Array.from(e.dataTransfer.files));
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.add('border-purple-500', 'bg-purple-500/10');
                }}
                onDragLeave={(e) => {
                  e.currentTarget.classList.remove('border-purple-500', 'bg-purple-500/10');
                }}
              >
                <input
                  id="quick-upload-input"
                  type="file"
                  accept=".wav,.mp3,.m4a,.aiff,.flac"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                />
                <Upload className="w-6 h-6 mx-auto mb-2 text-purple-400" />
                <p className="text-sm text-purple-400 font-medium">Drop files here to upload</p>
                <p className="text-xs text-stone-500 mt-1">to {selectedInstrument.name}</p>
              </div>
            ) : (
              <button
                onClick={() => setShowUploadModal(true)}
                className="w-full py-2 px-4 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 font-medium flex items-center justify-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Upload {columnLabels.samples}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-stone-900 rounded-xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold mb-4">{editingItem ? 'Edit Category' : 'Add Category'}</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-stone-400 mb-1 block">Name *</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full bg-stone-800 rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="e.g., Brasilian, Cuban, Capoeira"
                />
              </div>
              <div>
                <label className="text-sm text-stone-400 mb-1 block">Icon (click to select or type)</label>
                <div className="flex gap-2 mb-2 flex-wrap">
                  {['🇧🇷', '🇨🇺', '🇯🇲', '🇺🇸', '🇵🇷', '🥁', '🎵', '🎶', '🎷', '🎺', '🎸', '🪘', '🔔', '🎭', '⚔️', '🌴'].map(emoji => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => setFormData({...formData, icon: emoji})}
                      className={`text-2xl p-2 rounded-lg hover:bg-stone-700 ${formData.icon === emoji ? 'bg-amber-500/30 ring-2 ring-amber-500' : 'bg-stone-800'}`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={formData.icon || ''}
                  onChange={(e) => setFormData({...formData, icon: e.target.value})}
                  className="w-full bg-stone-800 rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="Or type/paste any emoji"
                />
              </div>
              <div>
                <label className="text-sm text-stone-400 mb-1 block">Description</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full bg-stone-800 rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-amber-500"
                  rows={2}
                />
              </div>
              <div>
                <label className="text-sm text-stone-400 mb-1 block">Order</label>
                <input
                  type="number"
                  value={formData.order || 0}
                  onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})}
                  className="w-full bg-stone-800 rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => { setShowCategoryModal(false); setEditingItem(null); }} className="flex-1 py-2 bg-stone-700 rounded-lg font-bold">
                Cancel
              </button>
              <button onClick={saveCategory} className="flex-1 py-2 bg-amber-500 text-black rounded-lg font-bold">
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ensemble Modal */}
      {showEnsembleModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-stone-900 rounded-xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold mb-4">{editingItem ? 'Edit Ensemble' : 'Add Ensemble'}</h3>
            <div className="space-y-4">
              {/* Category Selection - Independent */}
              <div>
                <label className="text-sm text-stone-400 mb-1 block">Category *</label>
                <select
                  value={formData.category_id || selectedCategory?.id || ''}
                  onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                  className="w-full bg-stone-800 rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-stone-400 mb-1 block">Name *</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full bg-stone-800 rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="e.g., Escola de Samba, Bloco Afro"
                />
              </div>
              <div>
                <label className="text-sm text-stone-400 mb-1 block">Icon (click to select or type)</label>
                <div className="flex gap-2 mb-2 flex-wrap">
                  {['🎭', '🎪', '🏛️', '🎵', '🎶', '🪘', '🥁', '🎺', '🎷', '🎸', '🎻', '🪕'].map(emoji => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => setFormData({...formData, icon: emoji})}
                      className={`text-2xl p-2 rounded-lg hover:bg-stone-700 ${formData.icon === emoji ? 'bg-cyan-500/30 ring-2 ring-cyan-500' : 'bg-stone-800'}`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={formData.icon || ''}
                  onChange={(e) => setFormData({...formData, icon: e.target.value})}
                  className="w-full bg-stone-800 rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="Or type/paste any emoji"
                />
              </div>
              <div>
                <label className="text-sm text-stone-400 mb-1 block">Description</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full bg-stone-800 rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-cyan-500"
                  rows={2}
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => { setShowEnsembleModal(false); setEditingItem(null); }} className="flex-1 py-2 bg-stone-700 rounded-lg font-bold">
                Cancel
              </button>
              <button 
                onClick={saveEnsemble} 
                disabled={!formData.category_id && !selectedCategory}
                className="flex-1 py-2 bg-cyan-500 text-black rounded-lg font-bold disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instrument Modal */}
      {showInstrumentModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-stone-900 rounded-xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold mb-4">{editingItem ? 'Edit Instrument' : 'Add Instrument'}</h3>
            <div className="space-y-4">
              {/* Category Selection */}
              <div>
                <label className="text-sm text-stone-400 mb-1 block">Category *</label>
                <select
                  value={formData.category_id || selectedCategory?.id || ''}
                  onChange={(e) => {
                    setFormData({...formData, category_id: e.target.value, ensemble_id: ''});
                    // Load ensembles for this category
                    if (e.target.value) loadEnsembles(e.target.value);
                  }}
                  className="w-full bg-stone-800 rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              </div>
              {/* Ensemble Selection */}
              <div>
                <label className="text-sm text-stone-400 mb-1 block">Ensemble *</label>
                <select
                  value={formData.ensemble_id || selectedEnsemble?.id || ''}
                  onChange={(e) => setFormData({...formData, ensemble_id: e.target.value})}
                  className="w-full bg-stone-800 rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Select an ensemble</option>
                  {ensembles.map(ens => (
                    <option key={ens.id} value={ens.id}>{ens.icon} {ens.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-stone-400 mb-1 block">Name *</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full bg-stone-800 rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., Surdo, Caixa, Repinique"
                />
              </div>
              <div>
                <label className="text-sm text-stone-400 mb-1 block">Icon (click to select or type)</label>
                <div className="flex gap-2 mb-2 flex-wrap">
                  {['🥁', '🪘', '🔔', '🎵', '🎶', '🎺', '🎷', '🪇', '🪈', '🎸', '🎻', '🪕', '🎹', '🪗', '📯', '🔊'].map(emoji => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => setFormData({...formData, icon: emoji})}
                      className={`text-2xl p-2 rounded-lg hover:bg-stone-700 ${formData.icon === emoji ? 'bg-green-500/30 ring-2 ring-green-500' : 'bg-stone-800'}`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={formData.icon || ''}
                  onChange={(e) => setFormData({...formData, icon: e.target.value})}
                  className="w-full bg-stone-800 rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Or type/paste any emoji"
                />
              </div>
              <div>
                <label className="text-sm text-stone-400 mb-1 block">Description</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full bg-stone-800 rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-green-500"
                  rows={2}
                />
              </div>
              
              {/* Upload Samples Section - Inline */}
              <div className="mt-4 pt-4 border-t border-stone-700">
                <label className="text-sm text-green-400 mb-2 block font-medium flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Upload Samples (optional)
                </label>
                <div 
                  className="border-2 border-dashed border-stone-600 rounded-lg p-4 text-center hover:border-green-500 transition-colors cursor-pointer"
                  onClick={() => document.getElementById('instrument-sample-input').click()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const files = Array.from(e.dataTransfer.files);
                    setPendingFiles(prev => [...prev, ...files]);
                  }}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <input
                    id="instrument-sample-input"
                    type="file"
                    accept=".wav,.mp3,.m4a,.aiff,.flac"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      setPendingFiles(prev => [...prev, ...files]);
                    }}
                  />
                  <Upload className="w-8 h-8 mx-auto mb-2 text-stone-500" />
                  <p className="text-sm">Drop files here or click to browse</p>
                  <p className="text-xs text-stone-500 mt-1">WAV, MP3, M4A, AIFF, FLAC</p>
                </div>
                
                {/* Show pending files */}
                {pendingFiles.length > 0 && (
                  <div className="mt-3 space-y-1">
                    <p className="text-xs text-stone-400">{pendingFiles.length} file(s) ready to upload:</p>
                    {pendingFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-stone-800 rounded px-2 py-1 text-sm">
                        <span className="truncate flex-1">{file.name}</span>
                        <button 
                          onClick={() => setPendingFiles(prev => prev.filter((_, i) => i !== idx))}
                          className="text-red-400 hover:text-red-300 ml-2"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => { setShowInstrumentModal(false); setEditingItem(null); setPendingFiles([]); }} className="flex-1 py-2 bg-stone-700 rounded-lg font-bold">
                Cancel
              </button>
              <button 
                onClick={saveInstrument} 
                disabled={!(formData.ensemble_id || selectedEnsemble)}
                className="flex-1 py-2 bg-green-500 text-black rounded-lg font-bold disabled:opacity-50"
              >
                {pendingFiles.length > 0 ? `Save & Upload ${pendingFiles.length} Files` : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-stone-900 rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Upload {columnLabels.samples}</h3>
            
            {/* Independent Selection - Category → Ensemble → Instrument */}
            <div className="space-y-3 mb-4 p-3 bg-stone-800/50 rounded-lg">
              <div>
                <label className="text-sm text-stone-400 mb-1 block">Category</label>
                <select
                  value={formData.upload_category_id || selectedCategory?.id || ''}
                  onChange={(e) => {
                    setFormData({...formData, upload_category_id: e.target.value, upload_ensemble_id: '', upload_instrument_id: ''});
                    if (e.target.value) loadEnsembles(e.target.value);
                  }}
                  className="w-full bg-stone-800 rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-stone-400 mb-1 block">Ensemble</label>
                <select
                  value={formData.upload_ensemble_id || selectedEnsemble?.id || ''}
                  onChange={(e) => {
                    setFormData({...formData, upload_ensemble_id: e.target.value, upload_instrument_id: ''});
                    if (e.target.value) loadInstruments(e.target.value);
                  }}
                  className="w-full bg-stone-800 rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select an ensemble</option>
                  {ensembles.map(ens => (
                    <option key={ens.id} value={ens.id}>{ens.icon} {ens.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-stone-400 mb-1 block">Instrument *</label>
                <select
                  value={formData.upload_instrument_id || selectedInstrument?.id || ''}
                  onChange={(e) => setFormData({...formData, upload_instrument_id: e.target.value})}
                  className="w-full bg-stone-800 rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select an instrument</option>
                  {instruments.map(inst => (
                    <option key={inst.id} value={inst.id}>{inst.icon} {inst.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Naming Options */}
            <div className="mb-4 p-3 bg-stone-800 rounded-lg">
              <label className="text-sm text-stone-400 mb-2 block font-medium">File Naming</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="naming" 
                    value="original"
                    defaultChecked
                    className="accent-purple-500"
                  />
                  <span className="text-sm">Keep original file names</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="naming" 
                    value="auto"
                    className="accent-purple-500"
                  />
                  <span className="text-sm">Auto-generate names (e.g., surdo_001, surdo_002)</span>
                </label>
              </div>
            </div>
            
            <div 
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                (formData.upload_instrument_id || selectedInstrument) 
                  ? 'border-stone-600 hover:border-purple-500' 
                  : 'border-stone-700 opacity-50 cursor-not-allowed'
              }`}
              onClick={() => {
                if (formData.upload_instrument_id || selectedInstrument) {
                  document.getElementById('sample-upload-input').click();
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (formData.upload_instrument_id || selectedInstrument) {
                  handleFileUpload(Array.from(e.dataTransfer.files));
                }
              }}
              onDragOver={(e) => e.preventDefault()}
            >
              <input
                id="sample-upload-input"
                type="file"
                accept=".wav,.mp3,.m4a,.aiff,.flac"
                multiple
                className="hidden"
                onChange={(e) => handleFileUpload(Array.from(e.target.files))}
              />
              <Upload className="w-12 h-12 mx-auto mb-4 text-stone-500" />
              <p className="text-lg font-medium mb-2">
                {(formData.upload_instrument_id || selectedInstrument) 
                  ? 'Drop audio files here' 
                  : 'Select an instrument first'}
              </p>
              <p className="text-sm text-stone-500">WAV, MP3, M4A, AIFF, FLAC</p>
              <p className="text-xs text-purple-400 mt-2">Super Admin access only</p>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowUploadModal(false)} className="flex-1 py-2 bg-stone-700 rounded-lg font-bold">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
